import React, { useState, useEffect, useRef } from "react";
import { Button, Tag, message, Tooltip } from "antd";
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { platformApi } from "../../../../api";
import "./index.less";

const WorkInProgress = ({
  jobCard,
  currentUser,
  isMechanic = false,
  isCompleted = false,
  onWorkStatusChange,
}) => {
  const [timerRunning, setTimerRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const mechanicId =
    typeof jobCard?.mechanic === "object"
      ? jobCard?.mechanic?.id
      : jobCard?.mechanic || jobCard?.User_JobOrder_mechanicToUser?.id;

  const currentUserId =
    currentUser?.id || currentUser?.employeeId || currentUser?._id;

  const isAssignedMechanic =
    Boolean(
      currentUserId && mechanicId && String(currentUserId) === String(mechanicId)
    ) ||
    Boolean(currentUser?.role === "Mechanic") ||
    Boolean(currentUser?.department?.role === "Mechanic") ||
    Boolean(
      currentUser?.role === "CEO" || currentUser?.department?.role === "Supervisor"
    );

  const isMechanicProfile =
    isMechanic ||
    currentUser?.role === "Mechanic" ||
    currentUser?.department?.role === "Mechanic";

  useEffect(() => {
    if (!jobCard) return;

    let initialSeconds = jobCard.elapsedSeconds || 0;

    if (jobCard.timerRunning && jobCard.workStartTime) {
      const startTime = new Date(jobCard.workStartTime).getTime();
      const now = new Date().getTime();
      const currentSessionSeconds = Math.max(
        0,
        Math.floor((now - startTime) / 1000)
      );
      initialSeconds = (jobCard.elapsedSeconds || 0) + currentSessionSeconds;
      setTimerRunning(true);
    } else {
      setTimerRunning(false);
    }

    setSeconds(initialSeconds);
  }, [jobCard]);

  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerRunning]);

  const formatTimer = (totalSecs) => {
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  /** Mechanic Pause — stay on current status */
  const handlePause = async (e) => {
    if (e) e.stopPropagation();
    if (!isAssignedMechanic) {
      message.warning("Only the assigned mechanic can pause the work timer.");
      return;
    }
    setLoading(true);
    try {
      const res = await platformApi.post(
        `/api/jobOrder/pauseWork/${jobCard.id}`,
        {
          elapsedSeconds: seconds,
          revertToPrevious: false,
        }
      );
      if (res?.data?.code === 200 || res?.status === 200) {
        setTimerRunning(false);
        message.info("Work timer paused. Job stays on current status.");
        if (onWorkStatusChange) onWorkStatusChange();
      }
    } catch (err) {
      console.error("Error pausing timer", err);
      message.error("Failed to pause timer");
    } finally {
      setLoading(false);
    }
  };

  /** Non-mechanic Stop — revert to previous status (legacy Pause behavior) */
  const handleStop = async (e) => {
    if (e) e.stopPropagation();
    setLoading(true);
    try {
      const res = await platformApi.post(
        `/api/jobOrder/pauseWork/${jobCard.id}`,
        {
          elapsedSeconds: seconds,
          revertToPrevious: true,
          previousStatus: jobCard?.previousStatus,
        }
      );
      if (res?.data?.code === 200 || res?.status === 200) {
        setTimerRunning(false);
        const revertedStatus =
          res?.data?.response?.data?.jobStatus || jobCard?.previousStatus;
        message.info(
          revertedStatus
            ? `Work stopped. JobCard reverted to ${revertedStatus}.`
            : "Work stopped."
        );
        if (onWorkStatusChange) onWorkStatusChange();
      }
    } catch (err) {
      console.error("Error stopping timer", err);
      message.error("Failed to stop work");
    } finally {
      setLoading(false);
    }
  };

  /** Mechanic Resume on same WIP card after Pause */
  const handleResume = async (e) => {
    if (e) e.stopPropagation();
    if (!isAssignedMechanic) {
      message.warning("Only the assigned mechanic can resume the work timer.");
      return;
    }
    setLoading(true);
    try {
      const res = await platformApi.post(`/api/jobOrder/startWork/${jobCard.id}`);
      if (res?.data?.code === 200 || res?.status === 200) {
        setTimerRunning(true);
        message.success("Work resumed from previous elapsed time.");
        if (onWorkStatusChange) onWorkStatusChange();
      } else {
        message.error(res?.data?.response?.message || "Failed to resume work");
      }
    } catch (err) {
      console.error("Error resuming timer", err);
      message.error("Failed to resume work");
    } finally {
      setLoading(false);
    }
  };

  if (
    isCompleted ||
    jobCard?.jobStatus === "Work Completed" ||
    jobCard?.jobStatus === "Washing" ||
    jobCard?.jobStatus === "Washing and Final Inspection" ||
    jobCard?.jobStatus === "Final Inspection"
  ) {
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tag
          style={{
            fontSize: "14px",
            fontWeight: "600",
            fontFamily: "monospace",
            backgroundColor: "#f0fdf4",
            borderColor: "#bbf7d0",
            color: "#16a34a",
            padding: "4px 10px",
            borderRadius: "6px",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            margin: 0,
            height: "32px",
          }}
        >
          <CheckCircleFilled style={{ fontSize: "14px", color: "#16a34a" }} />
          Total Job Time:{" "}
          {formatTimer(jobCard?.elapsedSeconds || seconds || 0)}
        </Tag>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Tag
        style={{
          fontSize: "14px",
          fontWeight: "600",
          fontFamily: "monospace",
          backgroundColor: "#e6fcf5",
          borderColor: "#a3f3de",
          color: "#0d9488",
          padding: "4px 10px",
          borderRadius: "6px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          margin: 0,
          height: "32px",
        }}
      >
        <ClockCircleOutlined style={{ fontSize: "14px", color: "#0d9488" }} />
        {formatTimer(seconds)}
      </Tag>

      {isMechanicProfile && timerRunning ? (
        <Tooltip
          title={
            !isAssignedMechanic ? "Only assigned mechanic can pause" : ""
          }
        >
          <Button
            style={{
              backgroundColor: "#f59e0b",
              borderColor: "#f59e0b",
              color: "#fff",
              fontWeight: "500",
              height: "32px",
              borderRadius: "6px",
              padding: "0 14px",
            }}
            icon={<PauseCircleOutlined />}
            onClick={handlePause}
            disabled={!isAssignedMechanic || loading}
          >
            Pause
          </Button>
        </Tooltip>
      ) : null}

      {isMechanicProfile && !timerRunning && mechanicId ? (
        <Tooltip
          title={
            !isAssignedMechanic ? "Only assigned mechanic can resume" : ""
          }
        >
          <Button
            type="primary"
            style={{
              backgroundColor: "#10b981",
              borderColor: "#10b981",
              fontWeight: "500",
              height: "32px",
              borderRadius: "6px",
              padding: "0 14px",
            }}
            icon={<PlayCircleOutlined />}
            onClick={handleResume}
            disabled={!isAssignedMechanic || loading}
          >
            Resume
          </Button>
        </Tooltip>
      ) : null}

      {/* Non-mech Stop: show when running or paused (Start Work stays on JC toolbar only) */}
      {!isMechanicProfile && mechanicId ? (
        <Button
          type="primary"
          style={{
            backgroundColor: "#dc2626",
            borderColor: "#dc2626",
            color: "#fff",
            fontWeight: "500",
            height: "32px",
            borderRadius: "6px",
            padding: "0 14px",
          }}
          icon={<StopOutlined />}
          onClick={handleStop}
          disabled={loading}
        >
          Stop
        </Button>
      ) : null}
    </div>
  );
};

export default WorkInProgress;
